import { type AskPunttProjectFile } from "@mg/schemas/src/christo/askPunttProject";

const OFFICE_EXT = /\.(docx?|xlsx?|pptx?|ppsx?|odt|ods|odp|odg|odf)$/i;

/** Derive a human-readable filename from an https/s3 source URL or storage key. */
export function displayFileNameFromSource(
  sourceOrKey?: string | null,
): string | undefined {
  if (!sourceOrKey?.trim()) return undefined;
  const trimmed = sourceOrKey.trim();
  try {
    const segment = new URL(trimmed).pathname.split("/").filter(Boolean).pop();
    if (segment) return decodeURIComponent(segment);
  } catch {
    // s3://… and bare keys are not valid URLs for the URL constructor.
  }
  const path = trimmed.split("?")[0].split("#")[0];
  const segment = path.split("/").filter(Boolean).pop();
  if (!segment) return undefined;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Stable id for the same logical upload across office originals and samba's
 * converted PDF (mirrors `get_name_id` in samba: basename stem, minus "-context").
 */
export function referenceDocumentIdentity(sourceOrKey: string): string {
  const name = displayFileNameFromSource(sourceOrKey) ?? sourceOrKey.trim();
  const stem = name.split(/[?#]/)[0].replace(/\.[^.]+$/, "");
  return stem.replace(/-context$/i, "").toLowerCase();
}

function isPdfSource(sourceOrKey: string): boolean {
  const name = displayFileNameFromSource(sourceOrKey) ?? sourceOrKey;
  return /\.pdf$/i.test(name.split(/[?#]/)[0]);
}

function isOfficeSource(sourceOrKey: string): boolean {
  const name = displayFileNameFromSource(sourceOrKey) ?? sourceOrKey;
  return OFFICE_EXT.test(name.split(/[?#]/)[0]);
}

export function projectFileLatestVersion(
  file: AskPunttProjectFile,
): AskPunttProjectFile["versions"][number] | undefined {
  return (
    file.versions.find((v) => v.versionNumber === file.currentVersion) ??
    file.versions[file.versions.length - 1]
  );
}

/**
 * Whether a file version belongs in the Reviews sidebar. Orphan
 * `in_review` stubs (auto-review bookkeeping without a Catalyst ticket) stay
 * in Reference Documents — keep in sync with christo `listReviews`.
 */
export function isProjectFileVersionListedInReviews(
  version: AskPunttProjectFile["versions"][number] | undefined,
): boolean {
  if (!version) return false;
  if (version.reviewTicketId) return true;
  return version.reviewStatus === "completed";
}

/** Mirrors the Reviews panel gate in christo `listReviews` (inverse filter). */
export function isProjectFileUnderReview(file: AskPunttProjectFile): boolean {
  return isProjectFileVersionListedInReviews(projectFileLatestVersion(file));
}

/**
 * User-uploaded, versioned project files for Reference Documents. Hides files
 * currently under review (they belong in Reviews) and drops an office original
 * when the same upload already exists as a PDF on the project.
 */
export function projectReferenceFiles(
  files: AskPunttProjectFile[] | undefined,
): AskPunttProjectFile[] {
  const pdfIdentities = new Set<string>();
  for (const file of files ?? []) {
    for (const v of file.versions ?? []) {
      for (const src of [v.source, v.key]) {
        if (src && isPdfSource(src)) {
          pdfIdentities.add(referenceDocumentIdentity(src));
        }
      }
    }
  }

  return (files ?? []).filter((file) => {
    if (isProjectFileUnderReview(file)) return false;
    const latest = projectFileLatestVersion(file);
    const source = latest?.source ?? latest?.key;
    if (!source) return true;
    if (!isOfficeSource(source)) return true;
    return !pdfIdentities.has(referenceDocumentIdentity(source));
  });
}
